/**
 * KIOSTORE (dashboard) permission catalogue — mirror of the API's
 * `stores/static/web_permissions.py`. Both files MUST describe the same tree:
 * the API validates what it stores, the dashboard needs its own copy to render
 * labels, and neither repo can import the other.
 *
 * Three fixed levels — module → group → action:
 *
 *     { orders: { confirmation: { cancel: true, … }, … }, … }
 *
 * A module without sub-structure uses the single group `general`. Orders is
 * the one that needs the middle level: its actions only mean something inside
 * a pipeline step. `orders.general` holds what no step can own — creating,
 * exporting, printing labels, inviting a customer, and the row status menu
 * (which writes `status`, so it can cancel an order outside the cancel flow).
 *
 * These flags drive what the dashboard SHOWS. They are not enforced per
 * endpoint server-side; the API still only checks owner-or-staff access.
 */

export const WEB_PERMISSION_TREE = {
  orders: {
    general: ['create', 'export', 'print_bordereau', 'invite_customer', 'contact_status'],
    // No `refund` here. A confirmation-stage order is UNFULFILLED by
    // construction — stock is deducted at preparation — and
    // orderShowRefundAction() refuses a refund on goods that never went out,
    // so the flag could never grant anything. Refunds live on `pos` and `livre`.
    confirmation: ['view', 'confirm', 'assign', 'claim', 'edit', 'delete'],
    pre_expedition: ['view', 'send_carrier', 'edit', 'cancel'],
    // No `mark_shipped` here. It gated a control that walked an order from
    // pre-expedition to preparation, back when every step lived in one local
    // list. Each step is its own server query now and stamps its own step on
    // the rows it returns, so the control could never render. Handing the
    // parcel to the carrier is `pre_expedition.send_carrier`.
    preparation: ['view', 'stock_deduct', 'cancel'],
    // No `cancel` on `pos` and `livre`. Both are outcomes, not queues: the
    // order is settled at the register or in the customer's hands, and the way
    // back is a refund. No cancel control exists on those two steps, and the
    // API refuses a delivered parcel outright (400 delivered_parcel).
    pos: ['view', 'refund'],
    retour: ['view', 'return_scan', 'cancel'],
    livre: ['view', 'encaisser', 'refund'],
    // Not a pipeline step: a cart that never became an order. It lives under
    // `orders` because that is where the screen sits, and it owns its own
    // right — reading abandoned carts is reading customer contact details,
    // which no order step and no marketing right should imply.
    abandoned: ['view'],
  },
  products: { general: ['view', 'create', 'edit', 'delete'] },
  customers: { general: ['view', 'create', 'edit', 'delete'] },
  discounts: { general: ['view', 'create', 'edit', 'delete'] },
  shipping: { general: ['view', 'edit'] },
  marketing: { general: ['view', 'edit'] },
  analytics: { general: ['view'] },
  purchases: { general: ['view', 'create'] },
  affiliate: { general: ['view', 'edit'] },
  wallet: { general: ['view', 'withdraw'] },
  themes: { general: ['view', 'edit'] },
  channels: { general: ['view', 'edit'] },
  staff: { general: ['view', 'manage'] },
  // Staff remuneration. One action only: `manage`. A `view` would be a dead
  // flag — every payroll endpoint either mutates or exposes the whole team's
  // pay — and a staff reading their OWN balance goes through /my-earnings,
  // which needs no permission at all.
  payroll: { general: ['manage'] },
  settings: { general: ['view', 'edit'] },
};

/**
 * The eight flat keys the model used before, and the subtrees each covers.
 * READ DIRECTION ONLY: an old stored row is expanded so nobody loses access on
 * upgrade — a flat key granted the WHOLE step, so every action of it turns on.
 *
 * Nothing computes flat keys FROM the tree any more. The bundles below mix the
 * `orders.*` pipeline with unrelated modules, so a staff granted every order
 * step also satisfied `store_admin` (via `orders.pos`) and `shipping` (via
 * `orders.pre_expedition`) — which unlocked Discounts, Wallet, Themes,
 * Channels, Purchases, Affiliate and Shipping in the nav. Gating reads the
 * tree directly; see `isGranted()`.
 */
export const LEGACY_WEB_PERMISSION_MAP = {
  order_confirmation: ['orders.general', 'orders.confirmation'],
  preparation: ['orders.preparation', 'orders.retour'],
  shipping: ['orders.pre_expedition', 'orders.livre', 'shipping.general'],
  stock: ['products.general'],
  customer_support: ['customers.general'],
  // `orders.abandoned` rides here in the READ direction only: before the tree,
  // the abandoned-carts screen was gated on the flat `marketing` key, so a
  // pre-tree row must keep opening it. New rows grant it explicitly.
  marketing: ['marketing.general', 'orders.abandoned'],
  analytics: ['analytics.general'],
  store_admin: ['orders.pos', 'staff.general', 'settings.general', 'discounts.general',
    'wallet.general', 'themes.general', 'channels.general', 'purchases.general',
    'affiliate.general'],
  // Payroll has its own flat key rather than riding inside store_admin: a
  // bookkeeper's row must not expand into the wallet, the themes and the
  // channels.
  payroll: ['payroll.general'],
};

export function emptyWebPermissions() {
  const tree = {};
  for (const [module, groups] of Object.entries(WEB_PERMISSION_TREE)) {
    tree[module] = {};
    for (const [group, actions] of Object.entries(groups)) {
      tree[module][group] = Object.fromEntries(actions.map((a) => [a, false]));
    }
  }
  return tree;
}

// A nested payload always has objects as values; a flat one never does.
function isLegacy(stored) {
  const values = Object.values(stored ?? {});
  return values.length > 0 && values.every((v) => typeof v !== 'object' || v === null);
}

function expandLegacy(stored) {
  const tree = emptyWebPermissions();
  for (const [key, paths] of Object.entries(LEGACY_WEB_PERMISSION_MAP)) {
    if (!stored[key]) continue;
    for (const path of paths) {
      const [module, group] = path.split('.');
      for (const action of WEB_PERMISSION_TREE[module][group]) {
        tree[module][group][action] = true;
      }
    }
  }
  return tree;
}

export const VIEW_ACTION = 'view';

/**
 * `view` is the prerequisite of every other action of its group: you cannot
 * create a product on a screen you cannot open, so a group whose `view` is off
 * keeps nothing else. The repair only ever REVOKES — turning `view` on because
 * a sibling is granted would hand out page access the owner never granted.
 *
 * Groups with no `view` action are untouched: `orders.general` (create, export,
 * print, invite, contact status) belongs to whoever already reads a pipeline
 * step, and `payroll.general` has `manage` alone.
 */
function applyViewInvariant(tree) {
  for (const [module, groups] of Object.entries(WEB_PERMISSION_TREE)) {
    for (const [group, actions] of Object.entries(groups)) {
      if (!actions.includes(VIEW_ACTION) || tree[module][group][VIEW_ACTION]) continue;
      for (const action of actions) tree[module][group][action] = false;
    }
  }
  return tree;
}

/**
 * Any stored payload → the complete tree of booleans. Accepts the nested
 * shape, the old flat shape, and null. Unknown keys are dropped: the
 * catalogue is the only vocabulary.
 *
 * The `view` invariant is applied last, so an inconsistent row already in the
 * database reads back as what its holder can actually reach. Mirror of the
 * API's `normalize_web_permissions()`.
 */
export function normalizeWebPermissions(stored) {
  const source = stored ?? {};
  if (isLegacy(source)) return expandLegacy(source);

  const tree = emptyWebPermissions();
  for (const [module, groups] of Object.entries(WEB_PERMISSION_TREE)) {
    const storedModule = source[module];
    if (!storedModule || typeof storedModule !== 'object') continue;
    for (const [group, actions] of Object.entries(groups)) {
      const storedGroup = storedModule[group];
      if (!storedGroup || typeof storedGroup !== 'object') continue;
      for (const action of actions) {
        tree[module][group][action] = Boolean(storedGroup[action]);
      }
    }
  }
  return applyViewInvariant(tree);
}

/**
 * Flip one action of one group in the editor, keeping the `view` prerequisite
 * true in the other direction too: granting any action grants `view`, revoking
 * `view` revokes the whole group. Returns the new group object.
 */
export function toggleGroupAction(module, group, current, action) {
  const actions = WEB_PERMISSION_TREE[module]?.[group];
  if (!actions || !actions.includes(action)) return current;

  const next = { ...current, [action]: !current[action] };
  if (!actions.includes(VIEW_ACTION)) return next;

  if (action === VIEW_ACTION) {
    if (!next[VIEW_ACTION]) for (const a of actions) next[a] = false;
  } else if (next[action]) {
    next[VIEW_ACTION] = true;
  }
  return next;
}

/** `isAllowed(tree, 'orders.confirmation.cancel')` — one leaf, no guessing. */
export function isAllowed(tree, path) {
  const [module, group, action] = String(path).split('.');
  if (!module || !group || !action) return false;
  return Boolean(tree?.[module]?.[group]?.[action]);
}

/** Every leaf path of the catalogue, e.g. `orders.livre.encaisser`. */
export function allPermissionPaths() {
  const paths = [];
  for (const [module, groups] of Object.entries(WEB_PERMISSION_TREE)) {
    for (const [group, actions] of Object.entries(groups)) {
      for (const action of actions) paths.push(`${module}.${group}.${action}`);
    }
  }
  return paths;
}
